import React from 'react';
import { Link } from 'react-router-dom';
import { trackEvent, type AnalyticsEvent } from '../../utils/analytics';
import { cn } from '../../src/lib/utils';
type CtaButtonProps = {
  href: string;
  children: React.ReactNode;
  variant?: 'primary' | 'outline' | 'ghost';
  size?: 'md' | 'lg' | 'xl';
  className?: string;
  ariaLabel?: string;
  /** Analytics event fired on click */
  event?: AnalyticsEvent;
  /** Extra non-personal analytics parameters (e.g. placement, tool slug). */
  eventParams?: Record<string, string>;
  onClick?: () => void;
};
const VARIANTS: Record<NonNullable<CtaButtonProps['variant']>, string> = {
  primary: 'bg-lime text-ink shadow-sm hover:bg-[#2B1E7A] hover:shadow-md active:bg-lime-dim',
  outline: 'border border-hairline bg-panel text-text-primary hover:border-[#D8D4EA] hover:bg-[#FCFCFE] active:bg-elevated',
  ghost: 'text-text-secondary hover:text-text-primary'
};
const SIZES = {
  md: 'px-5 py-2.5 text-[13px]',
  lg: 'px-6 py-3.5 text-sm',
  xl: 'px-8 py-4 text-[15px]'
};

/**
 * Button-styled link. Internal paths ("/…") use client-side routing; "#…" anchors
 * stay in-page; everything else opens in a new tab.
 */
export function CtaButton({
  href,
  children,
  variant = 'primary',
  size = 'md',
  className,
  ariaLabel,
  event,
  eventParams,
  onClick
}: CtaButtonProps) {
  const classes = cn('inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg font-semibold tracking-[-0.005em]', 'transition-[background-color,color,border-color,box-shadow] duration-200 ease-out', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-ink', SIZES[size], VARIANTS[variant], className);
  const handleClick = () => {
    if (event) trackEvent(event, {
      destination: href,
      ...eventParams
    });
    onClick?.();
  };
  if (href.startsWith('/')) {
    return <Link to={href} aria-label={ariaLabel} onClick={handleClick} className={classes} data-id="element-491" data-mp="1a282fkgngnu3">
        {children}
      </Link>;
  }
  const isAnchor = href.startsWith('#');
  return <a href={href} aria-label={ariaLabel} onClick={handleClick} {...isAnchor ? {} : {
    target: '_blank',
    rel: 'noopener noreferrer'
  }} className={classes} data-id="element-492" data-mp="3f42bdiir8ypr">
      {children}
    </a>;
}